import { useState, useEffect, useRef } from "react";
import FormErrors from "@sharpana/ui/FormErrors";
import { login } from "../api_client/allauth";
import { Link } from "react-router-dom";
import { useConfig } from "../authHooks";
import ProviderList from "../social_account_providers/ProviderList";
import Button from "@sharpana/ui/Button";
import {
  authContainer,
  authViewportMobileLogin,
  authCard,
  authCardMobile,
  formGroup,
  linkText,
  forgotPasswordLink,
  compactFormGroup,
  linkTextCompact,
  forgotPasswordLinkCompact,
} from "./styles";
import { isMobileApp } from "@sharpana/shared/utils/web_environment";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [response, setResponse] = useState({ fetching: false, content: null });
  const config = useConfig();
  const hasProviders = config.data.socialaccount?.providers?.length > 0;

  // Hide OAuth providers when running inside React Native WebView
  const isInWebView = isMobileApp();
  const showProviders = hasProviders && !isInWebView;

  // Ref to the scrollable card to control/reset its scroll position in WebView
  const cardRef = useRef<HTMLDivElement>(null);
  const lastVVH = useRef<number | null>(null);

  // When inputs blur and keyboard is likely dismissed, reset card scroll to avoid lingering offset
  useEffect(() => {
    if (!isInWebView) return;
    const onFocusOut = () => {
      const active = document.activeElement as HTMLElement | null;
      const isInput = !!active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
      if (isInput) return;
      setTimeout(() => {
        cardRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    };
    document.addEventListener("focusout", onFocusOut, true);
    return () => document.removeEventListener("focusout", onFocusOut, true);
  }, [isInWebView]);

  // Also watch visual viewport height: when it increases (keyboard hiding), reset scroll
  useEffect(() => {
    if (!isInWebView || !window.visualViewport) return;
    const vv = window.visualViewport;
    const handler = () => {
      const h = vv.height;
      if (lastVVH.current !== null && h > lastVVH.current + 40) {
        cardRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }
      lastVVH.current = h;
    };
    vv.addEventListener("resize", handler);
    lastVVH.current = vv.height;
    return () => vv.removeEventListener("resize", handler);
  }, [isInWebView]);

  function submit() {
    setResponse({ ...response, fetching: true });
    login({ email, password })
      .then((content) => {
        setResponse((r) => {
          return { ...r, content };
        });
      })
      .catch((e) => {
        console.error(e);
        window.alert(e);
      })
      .then(() => {
        setResponse((r) => {
          return { ...r, fetching: false };
        });
      });
  }

  return (
    <div className={isInWebView ? authViewportMobileLogin : authContainer}>
      <div ref={isInWebView ? cardRef : undefined} className={isInWebView ? authCardMobile : authCard}>
        {isInWebView ? (
          <>
            {/* hr-like divider with "Or" in the middle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                margin: "0.5rem 0",
              }}
            >
              <hr
                style={{ flex: 1, border: "none", borderTop: "2px solid #444" }}
              />
              <span
                style={{
                  padding: "0 10px",
                  color: "#b0b0b0",
                  fontSize: "0.9rem",
                }}
              >
                Or sign in with email
              </span>
              <hr
                style={{ flex: 1, border: "none", borderTop: "2px solid #444" }}
              />
            </div>
            {/* <div
              style={{
                textAlign: "center",
                margin: "1rem 0",
                color: "#b0b0b0",
              }}
            >
              <p>Sign in with email</p>
            </div> */}
          </>
        ) : (
          <>
            <h2>Login</h2>

            {showProviders && (
              <>
                <ProviderList callbackURL="/account/provider/callback" />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "1rem 0",
                  }}
                >
                  <hr
                    style={{
                      flex: 1,
                      border: "none",
                      borderTop: "2px solid #444",
                    }}
                  />
                  <span
                    style={{
                      padding: "0 10px",
                      color: "#b0b0b0",
                      fontSize: "0.9rem",
                    }}
                  >
                    Or use email
                  </span>
                  <hr
                    style={{
                      flex: 1,
                      border: "none",
                      borderTop: "2px solid #444",
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}

        <FormErrors errors={(response as any).content?.errors} />

        <div className={isInWebView ? compactFormGroup : formGroup}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <FormErrors
            param="email"
            errors={(response as any).content?.errors}
          />
        </div>

        <div className={isInWebView ? compactFormGroup : formGroup}>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
          />
          <Link
            className={isInWebView ? forgotPasswordLinkCompact : forgotPasswordLink}
            to="/account/password/reset"
          >
            Forgot your password?
          </Link>
          <FormErrors
            param="password"
            errors={(response as any).content?.errors}
          />
        </div>

        <Button
          style={{ width: "100%" }}
          disabled={response.fetching}
          onClick={() => submit()}
        >
          Login
        </Button>

        <p className={isInWebView ? linkTextCompact : linkText}>
          No account? <Link to="/account/signup">Sign up here.</Link>
        </p>

        {!isInWebView && (
          <p className={linkText} style={{ marginBottom: 0 }}>
            <a 
              href="https://info.sharpana.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: "#888", 
                fontSize: "0.9em",
                textDecoration: "underline",
                // fontStyle: "italic"
              }}
            >
              What is Sharpana?
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
